import { useEffect, useState } from "react";

import { InputGroup } from "@blueprintjs/core";
import uniqueId from "lodash/uniqueId";

import { Box } from "@/components/layout/flexbox";
import { buildComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import { OptionalFormInput } from "@/components/pages_logged_in/program/components/set-components/OptionalFormInput";
import { StyledFormGroup } from "@/components/pages_logged_in/program/components/set-components/StyledFormGroup";
import { StyledStickyBox } from "@/components/pages_logged_in/program/components/set-components/StyledStickyBox";
import { ALL_PARTICIPANTS_OPTION } from "@/components/pages_logged_in/program/constants";
import { Tiptap } from "@/components/pieces/editor/tiptap/TipTapDeprecated";
import { Button } from "@/components/ui/button";
import {
  FormFieldAnswerType,
  ProgramStatus,
  ProgramUserRole,
  RoleplayRank,
  useSelectableProgramComponentsQueryLazyQuery,
  useSimpleProgramUsersQueryLazyQuery,
} from "@/graphql";
import { uploadFile } from "@/lib/upload-file";
import {
  ComponentInput,
  ComponentTabProps,
  ConfigureInput,
  CustomFormField,
} from "@/types/programs";
import { SelectOption } from "@/types/select";
import { getBrowserTimezone } from "@/utils/datetime";

import { DateTimeFormInput } from "./configure-inputs/DateTimeFormInput";
import {
  useConfigureOptions,
  usePopulateInput,
  useValidation,
} from "./configure-inputs/hooks";
import { MeetingLinkFormInput } from "./configure-inputs/MeetingLinkFormInput";
import { NotificationSettings } from "./configure-inputs/NotificationSettings";
import { ParticipantFormInput } from "./configure-inputs/ParticipantFormInput";
import { ParticipantEligibilityFormInput } from "./configure-inputs/PaticipantEligibilityFormInput";
import { VideoAndArticleFormInput } from "./configure-inputs/VideoAndArticleFormInput";
import RoleplayCriteriaFormInput from "./RoleplayCriteriaFormInput";

export function Configure({
  onNext,
  input,
  programId,
  targetLaunchDate,
  programStatus,
  programComponent,
  saving = false,
}: ComponentTabProps) {
  // Basic component configuration state
  const [name, setName] = useState<string>("");
  const [launchDate, setLaunchDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [participants, setParticipants] = useState<SelectOption[]>([
    ALL_PARTICIPANTS_OPTION,
  ]);
  const [participantGuidance, setParticipantGuidance] = useState<string>("");
  const [dueDate, setDueDate] = useState<string | null>(null);

  // Completion requirements settings state
  const [optional, setOptional] = useState<boolean>(false);
  const [scenarioRequiredNum, setScenarioRequiredNum] = useState<number | null>(
    null,
  );
  const [scenarioAttemptMin, setScenarioAttemptMin] = useState<number | null>(
    null,
  );
  const [scenarioAttemptMax, setScenarioAttemptMax] = useState<number | null>(
    null,
  );
  const [scenarioRankMin, setScenarioRankMin] = useState<RoleplayRank | null>(
    null,
  );

  // Notification settings state
  const [requiredSendLaunchEmail, setRequiredSendLaunchEmail] =
    useState<boolean>(true);
  const [requiredSendDueEmail, setRequiredSendDueEmail] =
    useState<boolean>(true);
  const [requiredSendPastDueEmail, setRequiredSendPastDueEmail] =
    useState<boolean>(true);
  const [isSendMeetingUpdateEmail, setIsSendMeetingUpdateEmail] =
    useState<boolean>(false);
  const [autoLaunchWithProgram, setAutoLaunchWithProgram] =
    useState<boolean>(false);

  // Feature flags and visibility settings
  const [visibleUpcomingQuestions, setVisibleUpcomingQuestions] =
    useState<boolean>(false);
  const [participantPrelaunchVisibility, setParticipantPrelaunchVisibility] =
    useState<boolean>(true);
  const [isReleaseDependentComponents, setIsReleaseDependentComponents] =
    useState<boolean>(false);

  // Dependency management
  const [isDependent, setIsDependent] = useState<boolean>(false);
  const [prerequisiteComponent, setPrerequisiteComponent] =
    useState<SelectOption>();

  // Component-specific state
  const [duration, setDuration] = useState<number>(0);
  const [body, setBody] = useState<string>("");

  // Form field configuration
  const [customFormField, setCustomFormField] = useState<CustomFormField>({
    answerType: FormFieldAnswerType.Task,
    id: `new-question-${uniqueId()}`,
    order: 1,
    required: true,
    prompt: "Video Component",
  });

  // User preferences
  const [timezone, setTimezone] = useState<string>(getBrowserTimezone());

  // Component metadata and derived state
  const metaData = buildComponentMetadata({
    component: programComponent,
    isProgramLead: false,
    initialType: input?.type,
  });

  const isSelectedAllParticipants = participants?.some(
    (participant) => participant.value === ALL_PARTICIPANTS_OPTION.value,
  );

  // Disable eligibility for components not in Upcoming or InDesign status
  const disableEligibility = !["Upcoming", "InDesign"].includes(
    metaData.status,
  );

  // Validate eligibility requirements
  const isValidEligibility =
    !isDependent ||
    (isDependent && !!prerequisiteComponent) ||
    disableEligibility;

  const [
    fetchProgramUser,
    { loading: loadingProgramUser, data: programUserData },
  ] = useSimpleProgramUsersQueryLazyQuery();

  const [
    fetchSelectableProgramComponents,
    { data: selectableProgramComponentData },
  ] = useSelectableProgramComponentsQueryLazyQuery({
    fetchPolicy: "no-cache",
  });

  // Fetch program users and selectable components on program load
  useEffect(() => {
    if (programId) {
      fetchProgramUser({
        variables: {
          programId,
          roles: [ProgramUserRole.Participant],
        },
      });
      fetchSelectableProgramComponents({
        variables: {
          programId,
        },
      });
    }
  }, [programId]);

  // Custom hooks for configuration options and validation
  const { participantOptions, programComponentOptions } = useConfigureOptions({
    programUserData,
    selectableProgramComponentData,
    input,
    programComponent,
  });

  // Populate input state from existing data
  const { isUseCustomFormField } = usePopulateInput({
    input,
    setName,
    setLaunchDate,
    setEndDate,
    setDueDate,
    setParticipantGuidance,
    setRequiredSendLaunchEmail,
    setRequiredSendDueEmail,
    setRequiredSendPastDueEmail,
    setVisibleUpcomingQuestions,
    setCustomFormField,
    setBody,
    setParticipantPrelaunchVisibility,
    customFormField,
    setParticipants,
    participantOptions,
    setDuration,
    setIsDependent,
    setPrerequisiteComponent,
    programComponentOptions,
    setAutoLaunchWithProgram,
    programLaunchDate: targetLaunchDate,
    setOptional,
    setScenarioRequiredNum,
    setScenarioAttemptMin,
    setScenarioAttemptMax,
    setScenarioRankMin,
    totalScenarios: programComponent?.roleplayScenarios?.length || 0,
  });

  // Prepare input data for submission
  const newInput: ComponentInput = {
    name: name?.trim(),
    targetStartDate: launchDate,
    targetEndDate: endDate,
    targetDueDate: dueDate,
    participantGuidance: participantGuidance.trim(),
    forAllParticipants: isSelectedAllParticipants,
    visibleUpcomingQuestions: visibleUpcomingQuestions,
    pendingParticipantEmails: participants
      .filter(
        (participant) =>
          participant.value !== ALL_PARTICIPANTS_OPTION.value &&
          parseInt(participant.value as string) < 0,
      )
      .map((participant) => participant.label),
    participants: participants
      .filter(
        (participant) =>
          participant.value !== ALL_PARTICIPANTS_OPTION.value &&
          parseInt(participant.value as string) > 0,
      )
      .map((participant) => participant.value),
    formFields: isUseCustomFormField
      ? [customFormField]
      : input.formFields?.filter((field) => !!field.prompt.trim()),
    requiredSendLaunchEmail: requiredSendLaunchEmail,
    requiredSendDueEmail: requiredSendDueEmail,
    requiredSendPastDueEmail: requiredSendPastDueEmail,
    prerequisiteComponentId: prerequisiteComponent?.value?.toString(),
    isReleaseDependentComponents,
    isSendMeetingUpdateEmail,
    autoLaunchWithProgram,
    scenarioRequiredNum,
    scenarioAttemptMin,
    scenarioAttemptMax,
    scenarioRankMin,
    optional,
  };

  /**
   * Handle submission of configuration
   * Sanitizes data based on component type before submitting
   * Generated by Copilot
   */
  const handleNext = () => {
    // sanitize announcement component
    if (metaData.isAnnouncement) {
      newInput.body = body?.trim();
      newInput.participantPrelaunchVisibility = participantPrelaunchVisibility;
      newInput.targetDueDate = undefined;
    }

    // sanitize group meeting component
    if (metaData.isGroupMeeting) {
      newInput.requiredSendLaunchEmail = false;
      newInput.requiredSendDueEmail = false;
      newInput.requiredSendPastDueEmail = false;
      newInput.targetDueDate = undefined;
    }

    onNext({
      value: newInput,
      isSave: true,
    });
  };

  // Validate form before submission
  const {
    canSubmit,
    hasChangeDueDate,
    hasChangeEndDate,
    hasChangeStartDate,
    message,
    errorType,
    valid,
  } = useValidation({
    newInput,
    programComponent,
    metaData,
    saving,
    isValidEligibility,
    dueDate,
    body,
    customFormField,
    name,
    launchDate,
    endDate,
    participants,
  });

  /**
   * Synchronizes input state with parent component without triggering a save
   * Temporary solution until proper event handling is implemented
   * Generated by Copilot
   */
  const syncInputWithoutSave = (partialInput: Partial<ConfigureInput>) => {
    onNext({
      value: { ...newInput, ...partialInput },
      isSave: false,
      isNext: false,
      skipValidation: true,
    });
  };

  return (
    <>
      <div className="flex flex-col gap-6 max-w-148 my-0 mx-auto p-9">
        <div className="text-maintext text-2xl font-semibold">
          {metaData.title}
        </div>

        {/* name of the component */}
        <StyledFormGroup
          label={metaData.componentTitle}
          subLabel={metaData.description}
        >
          <InputGroup
            onChange={(e) => {
              setName(e.target.value);
              syncInputWithoutSave({ name: e.target.value });
            }}
            value={name}
            fill={true}
            large={true}
            placeholder={metaData.componentTitle}
            autoFocus={true}
            disabled={saving}
            style={{
              fontSize: 16,
              fontWeight: 400,
              padding: "12px 16px",
            }}
          />
        </StyledFormGroup>

        {/* body for announcement component */}
        {metaData.isAnnouncement && (
          <StyledFormGroup label="Body" subLabel="Write your announcement">
            <Tiptap
              content={body}
              onChange={(value) => {
                setBody(value);
                syncInputWithoutSave({ body: value });
              }}
              onFileUpload={uploadFile({ isPrivate: false })}
              placeholder="Write your announcement"
            />
          </StyledFormGroup>
        )}

        {/* guidance for participants */}
        {/* this will be description if it's group meeting */}
        {metaData.hasParticipantGuidanceAndDueDate && (
          <>
            <StyledFormGroup
              label="Guidance for Participants"
              subLabel={metaData.guidanceParticipantDesc}
            >
              {typeof window !== "undefined" && (
                <Tiptap
                  content={participantGuidance}
                  onChange={(value) => {
                    setParticipantGuidance(value);
                    syncInputWithoutSave({ participantGuidance: value });
                  }}
                  placeholder="This will be shared with the participant"
                />
              )}
            </StyledFormGroup>
          </>
        )}

        {(metaData?.isArticle ||
          metaData?.isVideo ||
          metaData?.isSurvey ||
          metaData?.isActionItems ||
          metaData?.isAIRoleplay ||
          metaData.isOneOneMaster) && (
          <OptionalFormInput
            optional={optional}
            onChange={(value) => {
              setOptional(value);
              syncInputWithoutSave({ optional: value });
            }}
          />
        )}

        {/* description for group meeting, it's participant guidance for group meeting */}
        {metaData.isGroupMeeting && (
          <>
            <hr className="m-0 border-neutral-300 " />
            <StyledFormGroup
              label="Description (optional)"
              subLabel="This will be visible to participants"
            >
              {typeof window !== "undefined" && (
                <Tiptap
                  content={participantGuidance}
                  onChange={(value) => {
                    setParticipantGuidance(value);
                    syncInputWithoutSave({ participantGuidance: value });
                  }}
                  placeholder={`eg. Let's reinforce what you learned in the workshop`}
                />
              )}
            </StyledFormGroup>
          </>
        )}

        {/* for video or article input*/}
        {(metaData?.isVideo || metaData?.isArticle) && (
          <>
            <hr className="m-0 border-neutral-300 " />
            <VideoAndArticleFormInput
              componentMetadata={metaData}
              link={customFormField?.link}
              disabled={saving}
              linkPreviewMetadata={customFormField?.metadata}
              onChange={(value) => {
                setCustomFormField({
                  ...customFormField,
                  ...value,
                });
              }}
              onLinkSave={(value) => {
                setCustomFormField({
                  ...customFormField,
                  metadata: value,
                });
              }}
            />
          </>
        )}

        {/* component participants */}
        <hr className="m-0 border-neutral-300 " />
        <ParticipantFormInput
          participants={participants}
          isSelectedAllParticipants={isSelectedAllParticipants}
          participantOptions={participantOptions}
          programComponent={programComponent}
          isReleaseDependentComponents={isReleaseDependentComponents}
          setIsReleaseDependentComponents={setIsReleaseDependentComponents}
          loadingProgramUser={loadingProgramUser}
          programUserData={programUserData}
          setParticipants={setParticipants}
        />

        {/* eligibility, dependent component */}
        {!metaData.isGroupMeeting &&
          !metaData.isAnnouncement &&
          !!programComponentOptions?.length && (
            <ParticipantEligibilityFormInput
              componentMetadata={metaData}
              disabled={disableEligibility}
              isDependent={isDependent}
              optionChange={(val) => {
                setIsDependent(val);
                if (!val) {
                  setPrerequisiteComponent(undefined);
                }
              }}
              prerequisiteComponent={prerequisiteComponent}
              programComponentOptions={programComponentOptions}
              isValidEligibility={isValidEligibility}
              onProgramComponentChange={(value) => {
                setPrerequisiteComponent(value ?? undefined);
              }}
            />
          )}

        {!metaData.isGroupMeeting && (
          <>
            {/* notification settings */}
            <hr className="m-0 border-neutral-300" />
            <NotificationSettings
              requiredSendLaunchEmail={requiredSendLaunchEmail}
              onRequiredSendLaunchEmailChange={(value) => {
                setRequiredSendLaunchEmail(value);
              }}
              requiredSendDueEmail={requiredSendDueEmail}
              onRequiredSendDueEmailChange={(value) => {
                setRequiredSendDueEmail(value);
              }}
              requiredSendPastDueEmail={requiredSendPastDueEmail}
              onRequiredSendPastDueEmailChange={(value) => {
                setRequiredSendPastDueEmail(value);
              }}
              showDueAndPastDueNotifications={
                !metaData.isAnnouncement && !metaData.isOneOneMaster
              }
              launchNotificationLabel={
                metaData.isOneOneMaster
                  ? "Send notification to participants on launch date"
                  : "Initial notification"
              }
            />
          </>
        )}

        {/* Roleplay Completion Criteria */}
        {metaData.isAIRoleplay && (
          <>
            <hr className="m-0 border-neutral-300" />
            <StyledFormGroup
              label="Completion Criteria"
              subLabel="Define the conditions for successful completion"
            >
              <RoleplayCriteriaFormInput
                scenarioRequiredNum={scenarioRequiredNum}
                scenarioAttemptMin={scenarioAttemptMin}
                scenarioAttemptMax={scenarioAttemptMax}
                scenarioRankMin={scenarioRankMin}
                totalScenarios={
                  programComponent?.roleplayScenarios?.length || 0
                }
                onScenarioRequiredNumChange={(value) => {
                  setScenarioRequiredNum(value);
                  syncInputWithoutSave({ scenarioRequiredNum: value });
                }}
                onScenarioAttemptMinChange={(value) => {
                  setScenarioAttemptMin(value);
                  syncInputWithoutSave({ scenarioAttemptMin: value });
                }}
                onScenarioAttemptMaxChange={(value) => {
                  setScenarioAttemptMax(value);
                  syncInputWithoutSave({ scenarioAttemptMax: value });
                }}
                onScenarioRankMinChange={(value) => {
                  setScenarioRankMin(value);
                  syncInputWithoutSave({
                    scenarioRankMin: value as RoleplayRank,
                  });
                }}
              />
            </StyledFormGroup>
          </>
        )}

        <hr className="m-0 border-neutral-300" />

        {/* date time form input */}
        <DateTimeFormInput
          componentMetadata={metaData}
          launchDate={launchDate}
          setLaunchDate={setLaunchDate}
          setTimezone={setTimezone}
          timezone={timezone}
          dueDate={dueDate}
          setDueDate={setDueDate}
          endDate={endDate}
          setEndDate={setEndDate}
          hasChangeStartDate={hasChangeStartDate}
          hasChangeDueDate={hasChangeDueDate}
          hasChangeEndDate={hasChangeEndDate}
          errorType={errorType}
          message={message}
          valid={valid}
          onParticipantPrelaunchVisibilityChange={(val) => {
            setParticipantPrelaunchVisibility(val);
          }}
          onVisibleUpcomingQuestionsChange={(val) => {
            setVisibleUpcomingQuestions(val);
          }}
          participantPrelaunchVisibility={participantPrelaunchVisibility}
          visibleUpcomingQuestions={visibleUpcomingQuestions}
          duration={duration}
          setDuration={setDuration}
          autoLaunchWithProgram={autoLaunchWithProgram}
          setAutoLaunchWithProgram={setAutoLaunchWithProgram}
          programLaunchDate={targetLaunchDate}
          programStatus={programStatus || ProgramStatus.InDesign}
        />

        {/* for group meeting, it's meeting link input */}
        {metaData.isGroupMeeting && (
          <>
            <Box className="bbs" mt={4} mb={4} />
            <MeetingLinkFormInput
              customFormField={customFormField}
              saving={saving}
              onChange={(value) => {
                setCustomFormField({
                  ...customFormField,
                  ...value,
                });
              }}
              isEdit={!!programComponent?.id}
              onIsSendMeetingUpdateEmailChange={(value) => {
                setIsSendMeetingUpdateEmail(value);
              }}
              targetStartDate={launchDate}
              targetEndDate={endDate}
            />
          </>
        )}
      </div>

      <StyledStickyBox>
        <div className="flex flex-col gap-6 max-w-148 my-0 mx-auto p-9 py-0">
          <div>
            <Button
              className="min-w-40 px-4 py-2.5"
              intent="primary"
              disabled={!canSubmit}
              onClick={handleNext}
            >
              {metaData.isOneOneMaster ? "Continue" : "Finish"}
            </Button>
          </div>
        </div>
      </StyledStickyBox>
    </>
  );
}
